import { buildingById, type Catalogue } from '../game/buildings/Catalogue';
import { countOf, iconOf, type Materials } from '../game/buildings/Materials';
import { clockOf, isNight } from '../game/sky/Daylight';
import { Weather } from '../game/sky/Weather';
import type { Item, Summary } from '../network/Summary';
import { loadBannerSpec, loadMyBanner, setHouse } from '../network/Banners';
import { bannerThumb, openBannerEditor } from './BannerEditor';
import { APP_VERSION } from '../version';

/**
 * Strona gracza — co widać po zalogowaniu i po wyjściu ze świata.
 *
 * The world is not entered by logging in. Between the door and the game there
 * is a page that says who you are, what stands in your name and where you will
 * land, and the world starts when the button is pressed. Two reasons, and the
 * second is the one that made it necessary: a player shown out for half an hour
 * of doing nothing has to land **somewhere** that is not the world, or the
 * client would simply walk back in and the half hour would have bought nothing.
 *
 * Plain DOM over the canvas, like the login screen and for the same reason:
 * this is text and buttons, and the game renders into a buffer where text would
 * be a smear.
 *
 * Everything here is read from one request — see `network/Summary`. Nothing on
 * this page writes anything, which is why looking at it does not put anybody
 * into the world.
 */

export type Choice = 'enter' | 'logout';

/** Where the reason for landing here is left, across the reload that brings us. */
const NOTICE_KEY = 'winged.notice';

/**
 * Leave a word for the page the next load will show.
 *
 * `sessionStorage` because the message has to survive exactly one reload and
 * nothing more: being shown out for idling is a fact about this tab, this time,
 * and a notice that outlived the browser would greet somebody with news from
 * last week.
 */
export function noteForPlayerPage(text: string): void {
  try {
    sessionStorage.setItem(NOTICE_KEY, text);
  } catch {
    // Private browsing refuses storage. Losing the explanation is a smaller
    // problem than refusing to leave the world.
  }
}

function takeNotice(): string | undefined {
  try {
    const text = sessionStorage.getItem(NOTICE_KEY);
    // Read once: a reload later, the reason is no longer the reason.
    sessionStorage.removeItem(NOTICE_KEY);
    return text ?? undefined;
  } catch {
    return undefined;
  }
}

export function showPlayerPage(
  container: HTMLElement,
  summary: Summary,
  dictionaries: { catalogue: Catalogue; materials: Materials },
  token: string
): Promise<Choice> {
  return new Promise((resolve) => {
    const page = document.createElement('div');
    page.className = 'home';

    const notice = takeNotice();

    page.innerHTML = `
      <div class="home-sheet">
        <h1>${escaped(summary.account.nickname)}</h1>
        ${notice ? `<p class="home-notice">${escaped(notice)}</p>` : ''}
        <div class="home-cards">
          ${card('Konto', account(summary))}
          ${card('Świat', world(summary))}
          ${card('Posiadłość', estate(summary, dictionaries))}
          ${card('Gdzie stoisz', standing(summary))}
          <section class="home-card home-banner"><h2>Sztandar</h2><div class="home-banner-body">…</div></section>
        </div>
        <div class="home-actions">
          <button type="button" class="home-enter">wejdź do świata</button>
          <button type="button" class="home-logout">wyloguj</button>
        </div>
        <p class="home-version">wersja ${escaped(APP_VERSION)}</p>
      </div>
    `;

    const enter = page.querySelector('.home-enter') as HTMLButtonElement;
    const logout = page.querySelector('.home-logout') as HTMLButtonElement;

    const answer = (choice: Choice) => {
      // Both buttons go dead on the first click: entering the world is a slow
      // thing to start, and starting it twice is two sessions of one player.
      enter.disabled = true;
      logout.disabled = true;
      page.remove();
      resolve(choice);
    };

    enter.addEventListener('click', () => answer('enter'));
    logout.addEventListener('click', () => answer('logout'));

    container.appendChild(page);
    enter.focus();
    void fillBanner(page.querySelector('.home-banner-body') as HTMLElement, container, token);
  });
}

/**
 * Karta sztandaru: gotowy sztandar, przycisk do jego stworzenia albo słowo,
 * że jeszcze nie przysługuje. Wypełniana osobno, bo pyta serwer, a reszta
 * strony nie ma na co czekać.
 */
async function fillBanner(body: HTMLElement, container: HTMLElement, token: string): Promise<void> {
  try {
    const [spec, mine] = await Promise.all([loadBannerSpec(), loadMyBanner(token)]);
    body.replaceChildren();

    if (mine.banner) {
      body.appendChild(bannerThumb(mine.banner, spec, 4));
      body.appendChild(houseLine(mine.banner.house, token));
      return;
    }
    if (!mine.allowed) {
      body.textContent = 'sztandar nie jest jeszcze dla ciebie';
      return;
    }

    const create = document.createElement('button');
    create.type = 'button';
    create.className = 'home-banner-create';
    create.textContent = 'stwórz sztandar';
    create.addEventListener('click', async () => {
      const made = await openBannerEditor(container, spec, token);
      if (made) {
        body.replaceChildren(bannerThumb(made, spec, 4), houseLine(made.house, token));
      }
    });
    const note = document.createElement('p');
    note.className = 'home-banner-note';
    note.textContent = 'raz i na zawsze — potem nie da się go zmienić';
    body.append(note, create);
  } catch {
    body.textContent = 'nie udało się wczytać sztandaru';
  }
}

/**
 * Ród pod sztandarem: nazwa, jeśli jest, albo pole do nadania go — raz.
 */
function houseLine(house: string | undefined, token: string): HTMLElement {
  const line = document.createElement('div');
  line.className = 'home-house';
  if (house) {
    line.textContent = `ród ${house}`;
    return line;
  }

  const input = document.createElement('input');
  input.type = 'text';
  input.maxLength = 24;
  input.placeholder = 'Własność rodu …';
  const give = document.createElement('button');
  give.type = 'button';
  give.className = 'home-banner-create';
  give.textContent = 'nadaj ród';
  const error = document.createElement('p');
  error.className = 'home-banner-note';

  give.addEventListener('click', async () => {
    const name = input.value.trim();
    if (!name) return;
    if (!window.confirm(`Nadać ród ${name}? Później nie da się go zmienić.`)) return;
    give.disabled = true;
    const answer = await setHouse(token, name).catch(
      () => ({ code: 'broken', message: 'serwer nie odpowiada' }) as const
    );
    if ('code' in answer) {
      error.textContent = answer.message;
      give.disabled = false;
      return;
    }
    line.replaceChildren(`ród ${answer.house}`);
  });

  const note = document.createElement('p');
  note.className = 'home-banner-note';
  note.textContent = 'ród pokazuje się pod nazwą twoich budynków: „Własność rodu …”';
  line.append(note, input, give, error);
  return line;
}

function card(title: string, rows: string): string {
  return `<section class="home-card"><h2>${title}</h2><dl>${rows}</dl></section>`;
}

function row(term: string, value: string): string {
  return `<dt>${term}</dt><dd>${value}</dd>`;
}

function account(summary: Summary): string {
  const { email, createdAt, lastSeen } = summary.account;

  return (
    row('adres', escaped(email)) +
    row('konto od', when(createdAt)) +
    row('ostatnio w świecie', lastSeen ? when(lastSeen) : 'jeszcze nigdy')
  );
}

function world(summary: Summary): string {
  const { id, latticeRadius, chunkCount, online, daylight } = summary.world;

  const time = `${clockOf(daylight.phase, daylight.nightShare)} · ${
    isNight(daylight.phase, daylight.nightShare) ? 'noc' : 'dzień'
  }${daylight.paused ? ' (doba zatrzymana)' : ''}`;

  // The sky is only sent when the panel has forced it; the rest of the time the
  // weather is a pure function of the world clock and a seed from the world's
  // name, and it lives on this side — see `Weather`. Reading the number here the
  // way the game reads it is what makes the page say what the player will
  // actually walk out into, instead of a second opinion about the sky.
  const forced = daylight.cover !== undefined;
  const cover = forced ? (daylight.cover as number) : new Weather(id, undefined).coverAt(daylight.clock);

  return (
    row('nazwa', escaped(id)) +
    row('rozmiar', `promień ${latticeRadius} · ${chunkCount} chunków`) +
    row('w świecie teraz', `${online}`) +
    row('pora dnia', time) +
    row('zachmurzenie', `${Math.round(cover * 100)}%${forced ? ' (wymuszone z panelu)' : ''}`)
  );
}

function estate(
  summary: Summary,
  { catalogue, materials }: { catalogue: Catalogue; materials: Materials }
): string {
  const { buildings, stored, purse } = summary.estate;

  // Grouped by what a thing is, not listed cell by cell: the page says "dwa
  // obozy drwali", and where each one stands is a question for the map.
  const counted = new Map<string, number>();
  for (const building of buildings) {
    const kind = buildingById(catalogue, building.kind);
    const level = kind?.levels[building.level - 1];
    const name = level?.label ?? kind?.label ?? `budynek ${building.kind}`;
    const label = building.ready ? name : `${name} (w budowie)`;
    counted.set(label, (counted.get(label) ?? 0) + 1);
  }

  const list =
    counted.size === 0
      ? row('budynki', 'jeszcze nic nie postawiłeś')
      : [...counted]
          .map(([label, count]) => row(escaped(label), `${count}`))
          .join('');

  // Ludność nad magazynami: osada jest przede wszystkim ludźmi. Wiersz znika,
  // gdy nie ma ani jednej osady — zero mieszkańców u kogoś, kto ma sam obóz,
  // byłoby liczbą o czymś, czego jeszcze nie ma.
  const people = summary.estate.people > 0 ? row('mieszkańcy', `${summary.estate.people}`) : '';

  return (
    list +
    people +
    row('w magazynach', goods(stored, materials)) +
    row('w sakwie', goods(purse, materials))
  );
}

function goods(items: Item[], materials: Materials): string {
  if (items.length === 0) return 'pusto';

  return items
    .map((item) => `${iconOf(materials, item.material)} ${countOf(materials, item.material, item.amount)}`)
    .join(', ');
}

function standing(summary: Summary): string {
  if (!summary.standing) {
    return row('miejsce', 'jeszcze nie wchodziłeś do tego świata — serwer wybierze miejsce');
  }

  const { q, r, elevation, label } = summary.standing;

  return (
    row('teren', label ? escaped(label) : 'nieznany') +
    row('wysokość', `${elevation}`) +
    row('pole', `${q}, ${r}`)
  );
}

/** A moment as somebody here reads it. */
function when(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime())
    ? '—'
    : at.toLocaleString('pl-PL', { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * The page is built from a string, and two of the things in it — a nickname and
 * an address — are typed by the person whose page it is. Escaped rather than
 * trusted: a nick with a tag in it would otherwise be markup on their own
 * screen, which is the cheap end of the same mistake.
 */
function escaped(text: string): string {
  const box = document.createElement('div');
  box.textContent = text;
  return box.innerHTML;
}
